// US-UI-MOBILE-LAYOUT -- reactive signal that returns true when the app
// should render its mobile-optimised layouts (master-detail split,
// single-column buckets, etc.) and false for desktop/landscape-tablet
// layouts.
//
// "Mobile" is defined as:
//   - Any device whose shortest viewport edge is ≤ 600px (phones in either
//     orientation), OR
//   - A viewport ≤ 1024px wide that is currently in portrait orientation
//     (tablets held in portrait). Landscape tablets and desktops always
//     get the all-in-one dashboard.
//
// The signal is reactive: subscribers re-run on viewport resize,
// orientation change, and preview-toggle changes.

import { createSignal, onCleanup, onMount } from 'solid-js';
import { mobilePreviewEnabled } from './mobilePreview';

export type LayoutClass =
  | 'phone-portrait'
  | 'phone-landscape'
  | 'tablet-portrait'
  | 'tablet-landscape';

function checkLayoutClass(): LayoutClass {
  if (typeof window === 'undefined') return 'tablet-landscape';
  const w = window.innerWidth;
  const h = window.innerHeight;
  // A phone stays a phone after rotation. Width-only checks misclassified
  // devices such as the Galaxy S22 Ultra (roughly 915x412 CSS px in
  // landscape) as tablets and switched them back to the legacy dashboard.
  if (Math.min(w, h) <= 600) return w > h ? 'phone-landscape' : 'phone-portrait';
  // Portrait tablet: ≤ 1024px wide AND taller than wide
  if (w <= 1024 && h > w) return 'tablet-portrait';
  return 'tablet-landscape';
}

const [viewportLayoutClass, setViewportLayoutClass] = createSignal<LayoutClass>(checkLayoutClass());

if (typeof window !== 'undefined') {
  let lastValue = viewportLayoutClass();
  const update = () => {
    const next = checkLayoutClass();
    if (next !== lastValue) {
      lastValue = next;
      setViewportLayoutClass(next);
    }
  };
  // Both events needed: resize fires on window drag / split-screen resize,
  // orientationchange fires on device rotation.
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);
}

/**
 * Returns true when the app should render mobile-optimised layouts.
 * True for: phones (any orientation), portrait-mode tablets (≤ 1024px),
 * and the mobile-preview toggle in Settings.
 * False for: landscape tablets, desktops.
 */
export function isMobileLayout(): boolean {
  return mobilePreviewEnabled() || viewportLayoutClass() !== 'tablet-landscape';
}

/** The concrete responsive layout, used when phone landscape and tablet portrait
 * need different chrome rather than the shared touch-first shell. */
export function layoutClass(): LayoutClass {
  return mobilePreviewEnabled() ? 'phone-portrait' : viewportLayoutClass();
}

/**
 * Component-scoped hook variant of isMobileLayout().
 * Provided for conventional hook-style usage in components.
 */
export function useIsMobileLayout(): () => boolean {
  onMount(() => {});
  onCleanup(() => {});
  return isMobileLayout;
}
